import { animate } from 'motion'
import { type RefObject, useCallback, useEffect, useRef } from 'react'

// Nothing switches and nothing loads until the strip has been still this long. Wheel, trackpad
// and scrubber events arrive a frame or two apart, so the wait has to outlast those gaps and
// still read as a stop. Rendering pages mid-flick is what made the strip choppy.
const REST_MS = 180
// Our own landing must still count as ours when the rest clock fires, so this outlasts it.
const OUR_OWN_SCROLL = REST_MS + 60
const ALREADY_THERE = 1
const FIRMEST = 700
const SOFTEST = 350
const SOFTER_PER_PIXEL = 0.8
const LONGEST_GLIDE_IN_SCREENS = 2.5

type Spring = ReturnType<typeof animate>

// A short snap is firm and a long glide is soft, and neither goes past its mark: the damping is
// the critical value for the stiffness, so the strip never bounces off a statement.
const springFor = (distance: number) => {
  const stiffness = Math.max(FIRMEST - distance * SOFTER_PER_PIXEL, SOFTEST)
  return {
    type: 'spring' as const,
    stiffness,
    damping: 2 * Math.sqrt(stiffness),
    restDelta: 1,
  }
}

// Nobody reads a file flying past for a second, and drawing it costs the frames the glide needs.
// A jump across thousands of pages lands a couple of screens short and springs in from there.
const glideStart = (from: number, to: number, longestGlide: number) => {
  if (Math.abs(to - from) <= longestGlide) return from
  return to < from ? to + longestGlide : to - longestGlide
}

const jumpsInstead = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export interface SpringScroll {
  springTo: (scrollLeft: number) => void
  cancel: () => void
  grip: () => void
  letGo: () => void
}

interface SpringScrollOptions {
  scrollRef: RefObject<HTMLElement | null>
  // The strip came to rest after the user moved it: pick a band and ease onto it.
  onSettle: (scrollLeft: number) => void
  // The strip came to rest after one of our own springs. Nothing to pick, but the pages that
  // ended up on screen still have to be drawn.
  onArrive: () => void
  // True on the first scroll of a burst, false again at rest. Twice a burst, never per event.
  onMotion: (moving: boolean) => void
}

// Scrolling tells nobody anything until it stops. One clock decides when it has stopped: every
// scroll event pushes the clock back, and a held scrubber thumb holds it off altogether.
export const useSpringScroll = ({
  scrollRef,
  onSettle,
  onArrive,
  onMotion,
}: SpringScrollOptions): SpringScroll => {
  const spring = useRef<Spring | null>(null)
  const ourOwnScroll = useRef(false)
  // A snap is the end of a move, not a new one, so the spring the settle starts says nothing.
  const snapping = useRef(false)
  const quietSpring = useRef(false)
  const ownScrollClock = useRef(0)
  const restClock = useRef(0)
  const gripped = useRef(false)
  const moving = useRef(false)
  const settle = useRef(onSettle)
  settle.current = onSettle
  const arrive = useRef(onArrive)
  arrive.current = onArrive
  const motion = useRef(onMotion)
  motion.current = onMotion

  const reportMotion = useCallback((nowMoving: boolean) => {
    if (moving.current === nowMoving) return
    moving.current = nowMoving
    motion.current(nowMoving)
  }, [])

  const stop = useCallback(() => {
    spring.current?.stop()
    spring.current = null
    window.clearTimeout(ownScrollClock.current)
  }, [])

  const cancel = useCallback(() => {
    stop()
    ourOwnScroll.current = false
    quietSpring.current = false
  }, [stop])

  const comeToRest = useCallback(() => {
    restClock.current = 0
    reportMotion(false)
    const element = scrollRef.current
    if (!element) return
    if (ourOwnScroll.current) {
      arrive.current()
      return
    }
    snapping.current = true
    settle.current(element.scrollLeft)
    snapping.current = false
  }, [reportMotion, scrollRef])

  const waitForRest = useCallback(() => {
    window.clearTimeout(restClock.current)
    if (gripped.current) return
    restClock.current = window.setTimeout(comeToRest, REST_MS)
  }, [comeToRest])

  // A thumb held on the scrubber is still scrubbing even when it stops moving, so the clock
  // waits for the release. A release with nothing scrolling after it still has to settle.
  const grip = useCallback(() => {
    gripped.current = true
    window.clearTimeout(restClock.current)
  }, [])

  const letGo = useCallback(() => {
    gripped.current = false
    waitForRest()
  }, [waitForRest])

  // Our own writes end in scroll events of their own. Counting those as ours for one rest period
  // is what keeps the strip from snapping again to the band it has just been put on.
  const landed = useCallback(() => {
    window.clearTimeout(ownScrollClock.current)
    ownScrollClock.current = window.setTimeout(() => {
      ourOwnScroll.current = false
    }, OUR_OWN_SCROLL)
  }, [])

  const springTo = useCallback(
    (scrollLeft: number) => {
      const element = scrollRef.current
      if (!element) return
      stop()
      ourOwnScroll.current = true
      quietSpring.current = snapping.current
      const from = glideStart(
        element.scrollLeft,
        scrollLeft,
        element.clientWidth * LONGEST_GLIDE_IN_SCREENS,
      )
      const distance = Math.abs(scrollLeft - from)

      if (distance < ALREADY_THERE || jumpsInstead()) {
        element.scrollLeft = scrollLeft
        landed()
        return
      }

      element.scrollLeft = from
      spring.current = animate(from, scrollLeft, {
        ...springFor(distance),
        onUpdate: (value) => {
          element.scrollLeft = value
        },
        onComplete: () => {
          element.scrollLeft = scrollLeft
          landed()
        },
      })
    },
    [landed, scrollRef, stop],
  )

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    const scrolled = () => {
      if (!quietSpring.current) reportMotion(true)
      waitForRest()
    }
    element.addEventListener('scroll', scrolled, { passive: true })

    return () => {
      window.clearTimeout(restClock.current)
      element.removeEventListener('scroll', scrolled)
    }
  }, [reportMotion, scrollRef, waitForRest])

  // Cancel, not stop: going away with a spring still marked as ours would swallow the next rest.
  useEffect(() => cancel, [cancel])

  return { springTo, cancel, grip, letGo }
}
